import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ComponentIcon from '../ComponentIcon';
import { COMPONENT_CATALOG } from '../../data/slideComponents';
import { SlideComponentType } from '../../data/slides';
import { appTheme } from '../../styles/theme';

const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${appTheme.surface};
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusLg};
  box-shadow: ${appTheme.shadowSm};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${appTheme.textMuted};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${appTheme.textMuted};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ComponentButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 10px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.surface};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${appTheme.green};
    background: ${appTheme.mint};
    box-shadow: ${appTheme.shadowSm};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const Label = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.text};
  text-align: left;
`;

interface ComponentsPanelProps {
  onAddComponent: (type: SlideComponentType) => void;
}

const ComponentsPanel: React.FC<ComponentsPanelProps> = ({ onAddComponent }) => {
  const { t } = useTranslation();

  return (
    <Panel aria-label={t('editor.components.ariaLabel')}>
      <Title>{t('editor.components.title')}</Title>
      <Hint>{t('editor.components.hint')}</Hint>
      <List>
        {COMPONENT_CATALOG.map((item) => (
          <ComponentButton
            key={item.type}
            type="button"
            title={t(`editor.components.descriptions.${item.type}`)}
            onClick={() => onAddComponent(item.type)}
          >
            <ComponentIcon type={item.type} />
            <Label>{t(`editor.components.catalog.${item.type}`)}</Label>
          </ComponentButton>
        ))}
      </List>
    </Panel>
  );
};

export default ComponentsPanel;
